import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddCircle, PersonAdd, RemoveCircle } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddBoxIcon from '@mui/icons-material/AddBox';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import { MuiTelInput } from 'mui-tel-input';
import SignaturePad from 'react-signature-canvas';
import { PatternFormat } from 'react-number-format';
import dateFormat from 'dateformat';
import { Country, Nationality, State, useCheckInInsertV2Mutation } from '../../generated/graphql';
import {
  OCRhelper,
  b64toBlob,
  mapOCRState,
  matchEnumOption,
} from '../../Components/Hooks/OCRhelper';
import { useSnackBar } from '../../Components/Hooks/useSnackBar';
import SnackBarMsg from '../../Components/SnackBar/SnackBarMsg';
import { TermsDialog } from '../DialogComponent/TermsDialog';
import { ValidateDob } from '../../ValidateDob';
import Loading from '../../Components/Loading/Loading';
import moment from 'moment';

interface Props {
  state: any;
  ExpandMore: any;
  expanded: any;
  handleExpandClick: any;
  dispatch: any;
  HotelDetails: any;
  handlePlus: any;
  handleMinus: any;
  localState: any;
  URLHotelID: any;
  URLBookingID: any;
  SharerNo: any;
  index: any;
  IsOnlinePayment: any;
  BookingStatus: boolean;
  Status: any;
  SelfCheckin: any;
  SelfChooseRoom: any;
  EnableSelfie: any;
  PreCheckinAdvTime: any;
  PreRegAdvTime: any;
  ArrivalDate: any;
}

const NationalityEnum: string[] = Object.values(Nationality).map(i => i.replace(/_/g, ' '));
const CountryEnum: any = Object.values(Country).map(i => i.replace(/_/g, ' '));
const StateEnum: any = Object.values(State).map(i => i.replace(/_/g, ' '));

const toTitleCase = (value?: string) =>
  (value || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** Map Google Vision / MyKad OCR result onto a guest ContactInfo object. */
const applyOCRToGuest = (guest: any, ocr: any) => {
  if (!ocr) return guest;

  const next = {
    ...guest,
    Address: guest.Address ? { ...guest.Address } : {},
  };

  if (ocr.isMyKad && ocr.mykadno) {
    next.FullName = ocr.mykadname || next.FullName;
    next.NRIC = ocr.mykadno;
    next.Nationality = 'Malaysian';
    next.Address.address = ocr.mykadaddress || next.Address.address;
    next.Address.city = toTitleCase(ocr.mykadcity) || next.Address.city;
    next.Address.postCode = ocr.mykadpostcode || next.Address.postCode;
    next.Address.state = mapOCRState(ocr.mykadstate, StateEnum) || next.Address.state;
    next.Address.country = 'Malaysia';
    const dob = ValidateDob(ocr.mykadno);
    if (dob?.getDate()) {
      next.BirthDate = dob;
    }
  } else if (ocr.passportholdername && ocr.passportnumber) {
    next.FullName = ocr.passportholdername || next.FullName;
    next.NRIC = ocr.passportnumber;
    next.Nationality =
      matchEnumOption(ocr.passportnationality, NationalityEnum) || next.Nationality;
    next.Address.country =
      matchEnumOption(ocr.passportcountry, CountryEnum) || next.Address.country;
    if (ocr.passportbirthdate) {
      const dob = moment(ocr.passportbirthdate, 'YYMMDD');
      if (dob.isValid()) {
        next.BirthDate = dob.toDate();
      }
    }
  }

  return next;
};

export const CheckInForm = ({
  state,
  ExpandMore,
  expanded,
  handleExpandClick,
  dispatch,
  HotelDetails,
  handlePlus,
  handleMinus,
  localState,
  URLHotelID,
  URLBookingID,
  SharerNo,
  index,
  IsOnlinePayment,
  BookingStatus,
  Status,
  SelfCheckin,
  SelfChooseRoom,
  EnableSelfie,
  PreCheckinAdvTime,
  PreRegAdvTime,
  ArrivalDate
}: Props) => {
  const navigate = useNavigate();
  const { snackFunc, snackBarMessage, openSnackBar } = useSnackBar();

  const [uploadFile, setUploadFile] = useState<any>({});
  const [openTerm, setOpenTerm] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [change, setChange] = useState(false);
  const [prevFile, setPrevFile] = useState({ IcFront: '', IcBack: '' });
  const [ocrLoading, setOcrLoading] = useState(false);

  const signCanvasRefs = useRef<Record<string, any>>({});

  const [CheckInInsertV2, { loading: loadingInsert }] = useCheckInInsertV2Mutation({});

  const Registration = state?.Registrations?.filter(x => x?.SharerNo === SharerNo)[0];

  const getGuestKey = (sharerNo: any, index2: number) => `${sharerNo}-${index2}`;

  // The E-Signature must always start blank when entering the form (same as the
  // Terms/PDPA checkboxes). We clone and strip the signature fields locally so the
  // shared parent data (used by CheckInView to show the saved signature) is untouched.
  const withoutSignature = (list: any[]) =>
    (list ?? []).map((room: any) => ({
      ...room,
      ContactInfo: (room.ContactInfo || []).map((guest: any) => ({
        ...guest,
        SignatureUrl: null,
        blob: null,
        IsSignature: false,
      })),
    }));

  // Local form state — field changes stay here and never dispatch to the parent,
  // so typing does not re-render PreCheckIn and does not scroll the page.
  const [formGuest, setFormGuest] = useState<any[]>(() =>
    withoutSignature(localState?.Guest?.filter((x: any) => x?.SharerNo === state?.SharerNo) ?? [])
  );

  // Sync from parent only when the number of contacts changes (handlePlus / handleMinus).
  // Merges: preserves locally-typed values for existing contacts, adopts new/removed ones.
  useEffect(() => {
    const parentList = withoutSignature(
      localState?.Guest?.filter((x: any) => x?.SharerNo === state?.SharerNo) ?? []
    );
    const parentCount = parentList[0]?.ContactInfo?.length ?? 0;
    setFormGuest(prev => {
      const localCount = prev[0]?.ContactInfo?.length ?? 0;
      if (parentCount === localCount) return prev;
      return parentList.map((room: any) => {
        const localRoom = prev.find((r: any) => r.SharerNo === room.SharerNo);
        if (!localRoom) return room;
        const mergedContactInfo = room.ContactInfo.map(
          (parentContact: any, idx: number) => localRoom.ContactInfo[idx] ?? parentContact
        );
        return { ...localRoom, ContactInfo: mergedContactInfo };
      });
    });
  }, [localState?.Guest]);

  const checkNationality = (sharerNo: any, index2: number) =>
    formGuest.find((g: any) => g?.SharerNo === sharerNo)?.ContactInfo?.[index2]?.Nationality;

  // Clones local form state (does NOT touch parent state).
  const cloneFormGuest = () =>
    formGuest.map((room: any) => ({
      ...room,
      ContactInfo: (room.ContactInfo || []).map((guest: any) => ({
        ...guest,
        Address: guest.Address ? { ...guest.Address } : {},
        Gallery: guest.Gallery ? [...guest.Gallery] : [],
      })),
    }));


  const handleOpenTerm = (type: any) => {
    setDialogType(type);
    setOpenTerm(true);
  };

  const handleCloseTerm = () => {
    setOpenTerm(false);
  };

  const onSubmit = (sharerNo) => {
    const inputEdit: any = [];
    const inputAdd: any = [];

    formGuest?.filter(x => x?.SharerNo === sharerNo)?.map(g => {
      g?.ContactInfo?.forEach(c => {
        const dateBirth = dateFormat(c.BirthDate, 'yyyy-mm-dd');
        if (c.Mode === 'add') {
          inputAdd.push({
            RegistrationID: Registration?.ID,
            FullName: c.FullName,
            Email: c.Email,
            OldFullName: c.OldFullName,
            NRIC: c.NRIC,
            OldNRIC: c.OldNRIC,
            BirthDate: `${dateBirth}T00:00:00.000Z`,
            MobileNo: c.MobileNo,
            Address: c.Address,
            IsTerm: true,
            IsPreCheckIn: false,
            Nationality: c.Nationality,
            IcPhotoFront: c?.IcPhotoFront,
            IcPhotoBack: c?.IcPhotoBack,
            Signature: c?.blob,
            SpecialRequest: c?.SpecialRequest,
          });
        } else {
          inputEdit.push({
            RegistrationID: c.Registration,
            ContactID: c.ContactID,
            FullName: c.FullName,
            Email: c.Email,
            OldFullName: c.OldFullName,
            NRIC: c.NRIC,
            OldNRIC: c.OldNRIC,
            BirthDate: `${dateBirth}T00:00:00.000Z`,
            MobileNo: c.MobileNo,
            Address: c.Address,
            Nationality: c.Nationality,
            IcPhotoFront: c?.IcPhotoFront?.ID ? undefined : c?.IcPhotoFront,
            IcPhotoBack: c?.IcPhotoBack?.ID ? undefined : c?.IcPhotoBack,
            IsTerm: true,
            Signature: c?.blob,
            IsPreCheckIn: false,
            SpecialRequest: c?.SpecialRequest,
          });
        }
      });
    });

    CheckInInsertV2({
      variables: {
        BookingID: URLBookingID,
        HotelID: URLHotelID,
        EditInformationInput: inputEdit,
        AddInformationInput: inputAdd,
      },
    })
      .then((r: any) => {
        if (r.data.CheckInInsertV2 === true) {
          navigate('/pre-checkin/form/room', {
            state: {
              BookingID: URLBookingID,
              HotelID: URLHotelID,
              FullName: Registration?.Guest?.Contact?.FullName,
              RegistrationID: Registration?.ID,
              IsOnlinePayment,
              SelfCheckin,
              SelfChooseRoom,
              EnableSelfie,
              PreCheckinAdvTime,
              PreRegAdvTime,
              ArrivalDate
            },
          });
        }
      })
      .catch(() => {
        snackFunc('Pre CheckIn Fail!', false);
      });
  };

  return loadingInsert || ocrLoading ? (
    <Loading />
  ) : (
    <>
      {formGuest?.map((x: any, i: any) => (
        <div key={x?.SharerNo}>
          <Card
            sx={{
              mb: '14px',
              borderRadius: '4px',
              boxShadow:
                '0 3px 1px -2px transparent, 2px 5px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)',
            }}
          >
            <CardActions key={x.SharerNo} disableSpacing style={{ backgroundColor: '#fff4dc' }}>
              <Box width="100%">
                <Box width="100%" display="flex">
                  <Typography fontSize="14px" fontWeight={600} color="#ff9800" align="left">
                    {'Room ' + Number(index + 1) + ' - ' + x.RoomType}
                  </Typography>
                </Box>
                <Box width="100%" display="flex">
                  <Box width="100%" display="flex">
                    <Typography
                      fontSize="14px"
                      fontWeight={500}
                      color="black"
                      align="left"
                      style={{ paddingTop: '10px' }}
                    >
                      {dateFormat(x.ContactInfo[0]?.ArrivalDate, 'dd mmm yyyy')} -{' '}
                      {dateFormat(x.ContactInfo[0]?.DepartureDate, 'dd mmm yyyy')}
                    </Typography>
                  </Box>
                  <Box key={x.SharerNo} display="flex" sx={{ justifyContent: 'flex-end' }}>
                    <IconButton style={{ pointerEvents: 'none' }}>
                      <PersonAdd sx={{ fontSize: 20, color: 'black' }} />
                    </IconButton>

                    <IconButton
                      aria-label="delete"
                      onClick={() => handleMinus(x?.SharerNo, i)}
                      sx={{ margin: 0 }}
                    >
                      <RemoveCircle
                        sx={{
                          fontSize: 20,
                          color:
                            x?.ContactInfo?.length === 1 ||
                            expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded === false
                              ? '#D3D3D3'
                              : '#f5ab35',
                        }}
                      />
                    </IconButton>

                    <Typography fontSize="14px" fontWeight={600} color="black" marginTop={1}>
                      {x?.ContactInfo?.length}
                    </Typography>

                    <IconButton
                      aria-label="add"
                      onClick={() => handlePlus(x.SharerNo, i)}
                      disabled={
                        expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded === false ||
                        expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.MaxGuest === x?.ContactInfo?.length
                      }
                      sx={{ margin: 0 }}
                    >
                      <AddCircle
                        sx={{
                          fontSize: 20,
                          color:
                            expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded === false ||
                            expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.MaxGuest === x?.ContactInfo?.length
                              ? '#D3D3D3'
                              : '#f5ab35',
                        }}
                      />
                    </IconButton>

                    <ExpandMore
                      expand={expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded}
                      onClick={() => handleExpandClick(x.SharerNo)}
                      aria-expanded={expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded}
                      aria-label="show more"
                      sx={{ fontSize: 20, color: 'black', margin: '0' }}
                    >
                      <ExpandMoreIcon />
                    </ExpandMore>
                  </Box>
                </Box>
              </Box>
            </CardActions>

            <Collapse
              key={i}
              in={expanded?.find((y: any) => y.SharerNo === x.SharerNo)?.IsExpanded}
              timeout="auto"
              unmountOnExit
            >
              <CardContent>
                {x?.ContactInfo?.map((k: any, index2: number) => (
                  <Box
                    key={index2}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <Grid key={index2} container spacing={0}>
                      <Grid item xs={0.5} sx={{ pt: '12px', fontSize: '14px' }}>
                        {index2 + 1 + '. '}
                      </Grid>
                      <Grid item xs={11}>
                        <Box
                          width="100%"
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '8px',
                            pb: '10px',
                            pt: '10px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography fontSize="12px" fontWeight={100} color="grey">
                            Attach IC/Passport*
                          </Typography>
                          <Typography fontSize="11px" fontWeight={400} color="#ff9800">
                            Auto-fills guest details
                          </Typography>
                        </Box>
                        <Box width="100%" sx={{ display: 'flex' }}>
                          <Box width="50%" display="flex">
                            <Typography fontSize="12px" fontWeight={100} color="grey" align="left">
                              Front
                            </Typography>
                          </Box>
                          <Box width="50%" display="flex">
                            <Typography fontSize="12px" fontWeight={100} color="grey">
                              Back
                            </Typography>
                          </Box>
                        </Box>

                        <Box width="100%" sx={{ display: 'flex', pb: '10px' }}>
                          <Box width="50%">
                            <Box
                              display="flex"
                              width="130px"
                              height="100px"
                              style={{ borderRadius: '5px' }}
                              component="label"
                            >
                              {(k?.IcPhotoFront && change === false) ||
                              uploadFile?.[getGuestKey(x.SharerNo, index2)]?.uploadFileFront ? (
                                <Box
                                  boxShadow="0 3px 1px -2px transparent, 2px 5px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)"
                                  style={{ borderRadius: '5px' }}
                                  className="margin-auto"
                                >
                                  <img
                                    src={
                                      uploadFile?.[getGuestKey(x.SharerNo, index2)]?.uploadFileFront ||
                                      k?.IcPhotoFront?.ImageURL
                                    }
                                    alt="IC Front"
                                    loading="lazy"
                                    height="100px"
                                    width="130px"
                                    style={{ borderRadius: '5px' }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  display="flex"
                                  component="span"
                                  width="130px"
                                  height="100px"
                                  style={{ borderRadius: '5px' }}
                                  sx={{ border: '1px dashed grey' }}
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <AddBoxIcon style={{ color: '#ff9800' }} />
                                </Box>
                              )}
                              <input
                                hidden
                                type="file"
                                accept="image/jpeg, image/png"
                                onChange={async (e: any) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (!file.type.startsWith('image/')) {
                                    snackFunc('Invalid File Format', false);
                                    return;
                                  }
                                  try {
                                    setOcrLoading(true);
                                    const OCRhelper2 = await OCRhelper(file);
                                    console.log('OCR result', OCRhelper2);
                                    const guestKey = getGuestKey(x.SharerNo, index2);
                                    const populated =
                                      OCRhelper2?.isMyKad ||
                                      (OCRhelper2?.passportholdername && OCRhelper2?.passportnumber);
                                    setFormGuest((prev: any[]) =>
                                      prev.map((room: any) =>
                                        room?.SharerNo === x?.SharerNo
                                          ? {
                                              ...room,
                                              ContactInfo: (room.ContactInfo || []).map(
                                                (guest: any, gi: number) =>
                                                  gi === index2
                                                    ? {
                                                        ...applyOCRToGuest(guest, OCRhelper2),
                                                        IcPhotoFront: OCRhelper2.Blob,
                                                        icphotoFront: OCRhelper2.Blob,
                                                      }
                                                    : guest
                                              ),
                                            }
                                          : room
                                      )
                                    );
                                    setUploadFile((prev: any) => ({
                                      ...prev,
                                      [guestKey]: {
                                        ...(prev[guestKey] || {
                                          uploadFileFront: '',
                                          uploadFileBack: '',
                                        }),
                                        uploadFileFront: OCRhelper2.picture,
                                      },
                                    }));
                                    setChange(true);
                                    if (populated) {
                                      snackFunc('Guest info auto-filled from IC/Passport', true);
                                    }
                                  } catch (err) {
                                    console.log(err);
                                    snackFunc('Failed to read IC/Passport. Please fill manually.', false);
                                  } finally {
                                    setOcrLoading(false);
                                    e.target.value = '';
                                  }
                                }}
                              />
                            </Box>
                          </Box>

                          <Box width="50%" mb="16px">
                            <Box
                              display="flex"
                              width="130px"
                              height="100px"
                              component="label"
                            >
                              {(k?.IcPhotoBack && change === false) ||
                              uploadFile?.[getGuestKey(x.SharerNo, index2)]?.uploadFileBack ? (
                                <Box
                                  boxShadow="0 3px 1px -2px transparent, 2px 5px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)"
                                  style={{ borderRadius: '5px' }}
                                  className="margin-auto"
                                >
                                  <img
                                    src={
                                      uploadFile?.[getGuestKey(x.SharerNo, index2)]?.uploadFileBack ||
                                      k?.IcPhotoBack?.ImageURL
                                    }
                                    alt="IC Back"
                                    loading="lazy"
                                    height="100px"
                                    width="130px"
                                    style={{ borderRadius: '5px' }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  display="flex"
                                  width="130px"
                                  height="100px"
                                  style={{ borderRadius: '5px' }}
                                  sx={{ border: '1px dashed grey' }}
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <AddBoxIcon style={{ color: '#ff9800' }} />
                                </Box>
                              )}
                              <input
                                hidden
                                type="file"
                                accept="image/jpeg, image/png"
                                onChange={async (e: any) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (!file.type.startsWith('image/')) {
                                    snackFunc('Invalid File Format', false);
                                    return;
                                  }
                                  const OCRhelper2 = await OCRhelper(file);
                                  const guestKey = getGuestKey(x.SharerNo, index2);
                                  setFormGuest((prev: any[]) =>
                                    prev.map((room: any) =>
                                      room?.SharerNo === x?.SharerNo
                                        ? {
                                            ...room,
                                            ContactInfo: (room.ContactInfo || []).map((guest: any, gi: number) =>
                                              gi === index2
                                                ? { ...guest, IcPhotoBack: OCRhelper2.Blob, icphotoBack: OCRhelper2.Blob }
                                                : guest
                                            ),
                                          }
                                        : room
                                    )
                                  );
                                  setUploadFile((prev: any) => ({
                                    ...prev,
                                    [guestKey]: {
                                      ...(prev[guestKey] || { uploadFileFront: '', uploadFileBack: '' }),
                                      uploadFileBack: OCRhelper2.picture,
                                    },
                                  }));
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', mb: '10px' }}>
                          <TextField
                            required
                            fullWidth
                            label="FullName"
                            type="text"
                            value={k?.FullName as string || ''}
                            name="FullName"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                            onChange={e => {
                              const newArr = cloneFormGuest();
                              const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                              select.FullName = e.target.value;
                              setFormGuest(newArr);
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', pb: '10px' }}>
                          <TextField
                            required
                            fullWidth
                            label="Email"
                            type="text"
                            value={k?.Email as string || ''}
                            name="Email"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                            onChange={e => {
                              const newArr = cloneFormGuest();
                              const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                              select.Email = e.target.value;
                              setFormGuest(newArr);
                            }}
                          />
                        </Box>

                        <Box width="100%" sx={{ display: 'flex', pb: '10px' }}>
                          <Box width="50%" pr="8px">
                            {checkNationality(x.SharerNo, index2) === 'Malaysian' ||
                            checkNationality(x.SharerNo, index2) === null ? (
                              <>
                                <PatternFormat
                                  required
                                  fullWidth
                                  customInput={TextField}
                                  label="ID No.(NRIC)"
                                  type="text"
                                  value={k?.NRIC}
                                  name="NRIC"
                                  format="######-##-####"
                                  mask="_"
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ style: { padding: '10px 0px 6px 6px' } }}
                                  onChange={e => {
                                    const newArr = cloneFormGuest();
                                    const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                    select.NRIC = e.target.value;
                                    setChange(true);
                                    setFormGuest(newArr);
                                  }}
                                  onBlur={() => {
                                    const newArr = cloneFormGuest();
                                    const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                    if (k?.NRIC !== null) {
                                      const validatedDate = ValidateDob(k?.NRIC);
                                      if (validatedDate?.getDate()) {
                                        select.BirthDate = validatedDate || null;
                                        setFormGuest(newArr);
                                      }
                                    }
                                  }}
                                />
                                {k?.NRIC !== null && !ValidateDob(k?.NRIC)?.getDate() && (
                                  <span style={{ color: 'red', fontSize: '12px' }}>Invalid NRIC</span>
                                )}
                              </>
                            ) : (
                              <TextField
                                required
                                fullWidth
                                label="ID No.(Passport)"
                                type="text"
                                value={k?.NRIC}
                                name="NRIC"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ style: { padding: '10px 0px 6px 6px' } }}
                                onChange={e => {
                                  const newArr = cloneFormGuest();
                                  const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                  select.NRIC = e.target.value;
                                  setFormGuest(newArr);
                                }}
                              />
                            )}
                          </Box>
                          <Box width="50%">
                            <TextField
                              required
                              fullWidth
                              label="Date of Birth"
                              type="date"
                              value={
                                checkNationality(x.SharerNo, index2) === 'Malaysian' && k?.NRIC
                                  ? ValidateDob(k.NRIC)?.getDate()
                                    ? dateFormat(ValidateDob(k.NRIC), 'yyyy-mm-dd')
                                    : ''
                                  : k?.BirthDate
                                  ? dateFormat(k?.BirthDate, 'yyyy-mm-dd')
                                  : ''
                              }
                              name="BirthDate"
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              onChange={e => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                select.BirthDate = e.target.value;
                                setFormGuest(newArr);
                              }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', pb: '10px' }}>
                          <MuiTelInput
                            label="Mobile No."
                            fullWidth
                            defaultCountry="MY"
                            value={k.MobileNo}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                            onChange={e => {
                              const newArr = cloneFormGuest();
                              const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                              select.MobileNo = e;
                              setFormGuest(newArr);
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', pb: '10px' }}>
                          <TextField
                            required
                            fullWidth
                            label="Address"
                            type="text"
                            value={k?.Address?.address || ''}
                            name="Address"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                            onChange={e => {
                              const newArr = cloneFormGuest();
                              const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                              select.Address ??= {};
                              select.Address.address = e.target.value;
                              setFormGuest(newArr);
                            }}
                          />
                        </Box>

                        <Box width="100%" sx={{ display: 'flex', pb: '10px' }}>
                          <Box width="50%" pr="8px">
                            <TextField
                              required
                              fullWidth
                              label="City"
                              type="text"
                              value={k?.Address?.city || ''}
                              name="City"
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                              onChange={e => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                select.Address.city = e.target.value;
                                setFormGuest(newArr);
                              }}
                            />
                          </Box>
                          <Box width="50%">
                            <TextField
                              required
                              fullWidth
                              label="Postcode"
                              type="text"
                              value={k?.Address?.postCode || ''}
                              name="Postcode"
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                              onChange={e => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                select.Address.postCode = e.target.value;
                                setFormGuest(newArr);
                              }}
                            />
                          </Box>
                        </Box>

                        <Box width="100%" sx={{ display: 'flex', pb: '10px' }}>
                          {k?.Address?.country === 'Malaysia' ? (
                            <Autocomplete
                              fullWidth
                              id="State"
                              options={StateEnum}
                              value={k?.Address?.state}
                              getOptionLabel={option => option || ''}
                              onChange={(_, data) => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                select.Address.state = data;
                                setFormGuest(newArr);
                              }}
                              size="small"
                              renderInput={params => (
                                <TextField
                                  required
                                  {...params}
                                  label="State"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          ) : (
                            <TextField
                              required
                              fullWidth
                              label="State"
                              type="text"
                              value={k?.Address?.state || ''}
                              name="State"
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                              onChange={e => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                select.Address.state = e.target.value;
                                setFormGuest(newArr);
                              }}
                            />
                          )}
                        </Box>

                        <Box width="100%" sx={{ display: 'flex', pb: '10px' }}>
                          <Box width="50%" pr="8px">
                            <Autocomplete
                              fullWidth
                              id="Nationality"
                              options={NationalityEnum}
                              value={k?.Nationality}
                              getOptionLabel={option => option}
                              onChange={(_, data) => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                select.Nationality = data;
                                setFormGuest(newArr);
                              }}
                              size="small"
                              renderInput={params => (
                                <TextField
                                  required
                                  {...params}
                                  label="Nationality"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Box>
                          <Box width="50%">
                            <Autocomplete
                              fullWidth
                              id="Country"
                              options={CountryEnum}
                              value={k?.Address?.country}
                              getOptionLabel={option => option || ''}
                              onChange={(_, data) => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                select.Address.country = data;
                                setFormGuest(newArr);
                              }}
                              size="small"
                              renderInput={params => (
                                <TextField
                                  required
                                  {...params}
                                  label="Country"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', pb: '10px' }}>
                          <TextField
                            fullWidth
                            label="Special Request"
                            type="text"
                            value={k?.SpecialRequest || ''}
                            name="SpecialRequest"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ style: { padding: '6px 0px 6px 6px' } }}
                            onChange={e => {
                              const newArr = cloneFormGuest();
                              const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                              select.SpecialRequest = e.target.value;
                              setFormGuest(newArr);
                            }}
                          />
                        </Box>

                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!k.IsTerm}
                                onChange={() => {
                                  const newArr = cloneFormGuest();
                                  const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                  if (!select) return;
                                  select.IsTerm = !select.IsTerm;
                                  if (!select.IsTerm) {
                                    select.SignatureUrl = null;
                                    select.blob = null;
                                    select.IsSignature = false;
                                    signCanvasRefs.current[getGuestKey(x.SharerNo, index2)]?.clear();
                                  }
                                  setFormGuest(newArr);
                                }}
                              />
                            }
                            label={
                              <Typography fontSize="13px" fontWeight={500}>
                                I acknowledge and accept the hotel{' '}
                                <Typography
                                  color="#ff9800"
                                  fontSize="13px"
                                  fontWeight={500}
                                  component="span"
                                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                  onClick={() => handleOpenTerm('T&C')}
                                >
                                  Terms and Condition
                                </Typography>
                              </Typography>
                            }
                          />
                        </FormGroup>

                        <FormGroup sx={{ mb: '10px' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!k.IsPDPA}
                                onChange={() => {
                                  const newArr = cloneFormGuest();
                                  const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                  if (!select) return;
                                  select.IsPDPA = !select.IsPDPA;
                                  setFormGuest(newArr);
                                }}
                              />
                            }
                            label={
                              <Typography fontSize="13px" fontWeight={500}>
                                I acknowledge and accept the hotel{' '}
                                <Typography
                                  color="#ff9800"
                                  fontSize="13px"
                                  fontWeight={500}
                                  component="span"
                                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                  onClick={() => handleOpenTerm('PDPA')}
                                >
                                  PDPA
                                </Typography>
                              </Typography>
                            }
                          />
                        </FormGroup>

                        <TermsDialog
                          HotelDetails={HotelDetails}
                          openTerm={openTerm}
                          handleCloseTerm={handleCloseTerm}
                          type={dialogType}
                        />

                        <Box width="100%" display="flex">
                          <Typography fontSize="12px" fontWeight={100} color="grey">
                            E-Signature
                          </Typography>
                        </Box>
                        <FormGroup sx={{ flexDirection: 'row-reverse' }}>
                          <div
                            onClick={() => {
                              const newArr = cloneFormGuest();
                              const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                              if (!select) return;
                              signCanvasRefs.current[getGuestKey(x.SharerNo, index2)]?.clear();
                              select.SignatureUrl = null;
                              select.blob = null;
                              select.IsSignature = false;
                              setFormGuest(newArr);
                            }}
                          >
                            <FormControlLabel
                              control={<RefreshIcon style={{ fontSize: '12px', color: 'grey' }} />}
                              label={
                                <Typography fontSize="12px" color="grey">
                                  Clear
                                </Typography>
                              }
                            />
                          </div>
                        </FormGroup>
                        <Box
                          height="100px"
                          display="flex"
                          mb="50px"
                          style={{ borderRadius: '5px', borderColor: 'grey' }}
                          sx={{ border: 1 }}
                          justifyContent="center"
                          alignItems="center"
                        >
                          {k?.Gallery?.find(y => y?.ID === k?.SignatureUrl)?.ImageURL || k?.SignatureUrl ? (
                            <img
                              src={k?.Gallery?.find(y => y?.ID === k?.SignatureUrl)?.ImageURL || k?.SignatureUrl}
                              alt="Signature"
                              loading="lazy"
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <SignaturePad
                              ref={ref => {
                                signCanvasRefs.current[getGuestKey(x.SharerNo, index2)] = ref;
                              }}
                              onEnd={() => {
                                const newArr = cloneFormGuest();
                                const select = newArr?.find((g: any) => g?.SharerNo === x?.SharerNo)?.ContactInfo[index2];
                                if (!select) return;
                                const guestKey = getGuestKey(x.SharerNo, index2);
                                const canvasRef = signCanvasRefs.current[guestKey];
                                if (!canvasRef) return;
                                const signUrl = canvasRef.getTrimmedCanvas().toDataURL('image/png');
                                const img = signUrl.slice(22);
                                const blob = b64toBlob(img, 'png', 512);
                                blob['name'] = 'Signature.png';
                                blob['lastModified'] = Date.now();
                                select.IsSignature = true;
                                select.SignatureUrl = signUrl;
                                select.blob = blob;
                                setFormGuest(newArr);
                              }}
                              canvasProps={{
                                className: 'm-b-8',
                                style: { width: '100%', height: '100%' },
                              }}
                            />
                          )}
                        </Box>

                        {index2 === x?.ContactInfo?.length - 1 ? (
                          <Box
                            style={{
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              onClick={() => {
                                if (Registration?.RegistrationStatus === 'Inhouse') {
                                  snackFunc(`Cannot save information. Registration already ${Registration?.RegistrationStatus}`, false);
                                } else {
                                  onSubmit(x.SharerNo);
                                }
                              }}
                              disabled={
                                !k.FullName ||
                                !k.Email ||
                                !k.NRIC ||
                                !k.BirthDate ||
                                !k.MobileNo ||
                                !k.Nationality ||
                                !k.Address?.address ||
                                !k.Address?.city ||
                                !k.Address?.postCode ||
                                !k.Address?.state ||
                                !k.Address?.country ||
                                !k.IcPhotoFront ||
                                !k.IcPhotoBack ||
                                !(k?.blob || k?.SignatureUrl) ||
                                k.IsPDPA === false ||
                                k.IsTerm === false ||
                                (checkNationality(x.SharerNo, index2) === 'Malaysian' &&
                                  k?.NRIC !== null &&
                                  !ValidateDob(k?.NRIC)?.getDate())
                              }
                              sx={{
                                backgroundColor:
                                  !k.FullName ||
                                  !k.Email ||
                                  !k.NRIC ||
                                  !k.BirthDate ||
                                  !k.MobileNo ||
                                  !k.Nationality ||
                                  !k.Address?.address ||
                                  !k.Address?.city ||
                                  !k.Address?.postCode ||
                                  !k.Address?.state ||
                                  !k.Address?.country ||
                                  !k.IcPhotoFront ||
                                  !k.IcPhotoBack ||
                                  !k.blob ||
                                  k.IsPDPA === false ||
                                  k.IsTerm === false ||
                                  (checkNationality(x.SharerNo, index2) === 'Malaysian' &&
                                    k?.NRIC !== null &&
                                    !ValidateDob(k?.NRIC)?.getDate())
                                    ? '#D3D3D3'
                                    : '#ff9800',
                                '&:hover': { backgroundColor: '#ff9800' },
                                '&:active': { backgroundColor: '#ff9800' },
                                paddingLeft: '40px',
                                paddingRight: '40px',
                              }}
                            >
                              <Typography style={{ color: '#ffffff' }}>Next</Typography>
                            </Button>
                          </Box>
                        ) : null}
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </CardContent>
            </Collapse>
          </Card>
          <SnackBarMsg open={openSnackBar} message={snackBarMessage} />
        </div>
      ))}
    </>
  );
};
